import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { ButtonGroup, ButtonTypes, LinkButton, RowButton, StandardButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { ErrorCard } from '@eika/card';
import { Icon, IconTypes } from '@eika/icon';
import { Input } from '@eika/input';
import { Modal } from '@eika/modal';

import { useDebitAccountsQuery } from '@/react-query/hooks/query/accounts/use-debit-accounts';

import { Format, useWatchError } from '@domain/app';
import ReceiverListItem from '@domain/pay/components/ReceiverField/ReceiverListItem';
import { useVerifyAccountOwner } from '@domain/pay/useVerifyAccountOwner';
import { useTranslations } from '@domain/translations';
import { DebitAccount } from '@generated-types';
import useText from '@hooks/use-text';
import { KbTransferForm } from '@pages/transactions/components/KbTransactions/components/TransferForm/TransferSchema';

import './ReceiverField.scss';

const ReceiverField = () => {
    const { data: accounts = [], error: queryError } = useDebitAccountsQuery({ retry: false });

    const t = useTranslations();
    const client = useQueryClient();
    const context = useFormContext();
    const [open, setOpen] = React.useState(false);
    const {
        formCancel,
        transferContinue,
        transferToModalTitle,
        transferTryAgain,
        transferToErrorTitle,
        transferToLabel,
    } = useText();
    const error = useWatchError<KbTransferForm>('account.number', 'pay.validation.payeeAccountNumber.required');
    const accountNumber = context.watch('account.number');

    const account = accounts.find((account) => account.number === accountNumber);

    const { data: ownershipData, isPending: isVerifyingOwnership } = useVerifyAccountOwner(accountNumber, {
        enabled: !account,
    });
    const ownershipSetRef = React.useRef(false);

    React.useEffect(() => {
        if (ownershipData !== undefined && accountNumber && !ownershipSetRef.current && !account) {
            context.setValue('account.ownership', ownershipData, {
                shouldValidate: true,
            });
            setTimeout(() => {
                context.trigger('account');
            }, 0);
            ownershipSetRef.current = true;
        }
    }, [ownershipData, accountNumber, context, account]);

    React.useEffect(() => {
        ownershipSetRef.current = false;
    }, [accountNumber]);

    const { placeholder } = t.pay.payeeField;

    const handleValueChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const rawValue = event.target.value.replace(/\D/g, '');
            context.setValue('account.number', rawValue, {
                shouldValidate: false,
            });
        },
        [context],
    );

    const handleClose = React.useCallback(() => {
        setOpen(false);
    }, []);

    const handleSubmit = React.useCallback(async () => {
        const valid = await context.trigger('account');

        if (valid && ownershipData !== undefined) {
            context.setValue('account.ownership', ownershipData, {
                shouldValidate: true,
            });
            handleClose();
        }
    }, [context, handleClose, ownershipData]);

    const handleRetry = React.useCallback(() => {
        client.resetQueries({ queryKey: ['DebitAccounts'] });
    }, [client]);

    const selectPaymentAccount = React.useCallback(
        (account: DebitAccount) => {
            context.setValue('account.ownership', true, { shouldValidate: false });

            context.setValue('account.number', account.number, {
                shouldValidate: true,
                shouldDirty: true,
            });
            context.clearErrors('account.ownership');
            handleClose();
        },
        [context, handleClose],
    );
    const formatedAccountNumber = Format.accountNumber(accountNumber, '.');

    return (
        <>
            <RowButton onClick={() => setOpen(true)}>
                <RowButton.AccountSelectorLayout
                    label={transferToLabel}
                    placeholder={placeholder}
                    accountAlias={account?.agreementName ?? account?.productName ?? undefined}
                    accountNumber={account?.number ? formatedAccountNumber : undefined}
                    error={error}
                    icon={<Icon type={IconTypes.LITEN_PIL_HOEYRE} />}
                />
            </RowButton>
            <Modal open={open} setOpen={setOpen} title={transferToModalTitle} contentShouldStretch>
                <div className="modal-content">
                    <Input
                        placeholder={placeholder}
                        inputMode="numeric"
                        className="select-receiver-form__search-input"
                        maxLength={35}
                        error={error}
                        value={Format.accountNumber(accountNumber || '', '.')}
                        onChange={handleValueChange}
                    />

                    {queryError && (
                        <div className="sgw-content">
                            <ErrorCard isModal title={transferToErrorTitle} icon={IconTypes.BANDAGED_HEART}>
                                <ButtonWithSpinner buttonType={ButtonTypes.STANDARD_POSITIVE} onClick={handleRetry}>
                                    {transferTryAgain}
                                </ButtonWithSpinner>
                            </ErrorCard>
                        </div>
                    )}
                    {accounts && (
                        <ul className="payment-form-fields__list">
                            {accounts.map((account) => (
                                <li key={Number(account.number)}>
                                    <ReceiverListItem account={account} onClick={() => selectPaymentAccount(account)} />
                                </li>
                            ))}
                        </ul>
                    )}
                    <ButtonGroup
                        primaryAction={
                            <StandardButton onClick={handleSubmit} disabled={!accountNumber || isVerifyingOwnership}>
                                {transferContinue}
                            </StandardButton>
                        }
                        secondaryAction={<LinkButton onClick={handleClose}>{formCancel}</LinkButton>}
                    />
                </div>
            </Modal>
        </>
    );
};

export default ReceiverField;
